import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight, Clock } from "lucide-react";
import { api, formatZAR } from "../lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("m_payment_id") || params.get("session_id");
  const type = params.get("type");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const maxAttempts = 12;

    const poll = async () => {
      try {
        // Try PayFast endpoint first (m_payment_id-based), fall back to Stripe
        let res;
        try {
          res = await api.get(`/payments/payfast/status/${sessionId}`);
        } catch {
          res = await api.get(`/payments/status/${sessionId}`);
        }
        const d = res.data;
        setData(d);
        if (d.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (d.status === "expired" || d.payment_status === "failed" || d.payment_status === "cancelled") {
          setStatus("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setStatus("pending");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2500);
      } catch {
        setStatus("error");
      }
    };
    poll();
  }, [sessionId]);

  const message = {
    donation: { title: "Thank you for your gift", sub: "Your donation helps raise the next generation of gospel artists." },
    membership: { title: "Welcome to Next Gen Praise", sub: "Your membership is active. Unlock exclusive content now." },
    product: { title: "Order confirmed", sub: "Check your email for your receipt and download link (if digital)." },
    event: { title: "Your spot is booked", sub: "We've emailed your access details for the event." },
  }[type] || { title: "Payment complete", sub: "Thank you!" };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-20 bg-[#F5F5F5]">
      <div className="max-w-lg w-full mx-auto px-6" data-testid="payment-success-page">
        <div className="bg-white p-10 text-center border-t-4 border-[#C00000]">
          {status === "checking" && (
            <>
              <Clock className="w-14 h-14 text-[#1F3864] mx-auto mb-5 animate-pulse" />
              <h1 className="font-display text-3xl text-[#1F3864] mb-3">Confirming your payment...</h1>
              <p className="text-gray-600">This will only take a moment.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-5" />
              <h1 className="font-display text-3xl text-[#1F3864] mb-3">{message.title}</h1>
              <p className="text-gray-600 mb-5">{message.sub}</p>
              {data?.amount && (
                <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-6">
                  Amount: {formatZAR(data.amount)}
                </div>
              )}
              <div className="flex gap-3 justify-center">
                <Link to="/" className="btn-outline-navy">Home</Link>
                <Link to={type === "membership" ? "/videos" : "/dashboard"} className="btn-primary-red" data-testid="action-payment-next-button">
                  {type === "membership" ? "Explore Videos" : "Dashboard"} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          )}
          {status === "pending" && (
            <>
              <Clock className="w-14 h-14 text-[#1F3864] mx-auto mb-5" />
              <h1 className="font-display text-3xl text-[#1F3864] mb-3">Payment processing</h1>
              <p className="text-gray-600 mb-6">Your payment is being processed. You'll receive email confirmation once complete.</p>
              <Link to="/" className="btn-primary-red">Home</Link>
            </>
          )}
          {(status === "expired" || status === "error") && (
            <>
              <XCircle className="w-14 h-14 text-[#C00000] mx-auto mb-5" />
              <h1 className="font-display text-3xl text-[#1F3864] mb-3">Payment not completed</h1>
              <p className="text-gray-600 mb-6">The session has expired or was cancelled. You can try again at any time.</p>
              <Link to="/" className="btn-primary-red">Back to Home</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
